import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  ArrowUpDown,
  ChevronDown,
  ChevronRight,
  FolderTree,
  Globe,
  PieChart,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  Users,
  WalletCards,
  X,
} from "lucide-react";
import { showToastMessage } from "utils";
import AdminPassPreview from "../components/AdminPassPreview";
import StatusBadge from "../components/StatusBadge";
import {
  generateWorkspaceCreatePasswordLinkV2,
  generateWorkspaceWalletLinkV2,
  getCustomerDetailV2,
  getCustomersV2,
  resendWorkspaceCreatePasswordV2,
  resendWorkspaceWalletEmailV2,
} from "./api";

function formatDays(value) {
  if (value == null) return "-";
  if (value < 0) return `${Math.abs(value)} overdue`;
  if (value === 0) return "Today";
  return `${value} days`;
}

function formatDate(value) {
  if (!value) return "-";
  return new Date(value).toLocaleDateString();
}

function formatRoleLabel(value) {
  if (!value) return "User";
  const normalized = String(value).toLowerCase();
  if (normalized === "personal user" || normalized === "free user") {
    return "Personal / Free User";
  }

  return String(value)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatAccountTypeLabel(value) {
  if (!value) return "Personal";
  return String(value)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatUrlLabel(value) {
  if (!value) return "-";
  return String(value).replace(/^https?:\/\//, "");
}

function isWorkspaceAdminRole(role) {
  return ["org_admin", "ent_admin"].includes(String(role || "").toLowerCase());
}

function getCustomerBucket(row) {
  if (!row) return "other";
  const role = String(row.role || "").toLowerCase();
  const accountType = String(row.accountType || "").toLowerCase();

  if (isWorkspaceAdminRole(role)) return "workspace_admin";
  if (["org_employee", "ent_employee"].includes(role)) return "employee";
  if (["organization", "enterprise"].includes(accountType)) return "managed_user";
  if (accountType === "personal") return "personal";
  if (!row.accountType) return "free";
  return "other";
}

function compareValues(left, right) {
  if (typeof left === "number" && typeof right === "number") {
    return left - right;
  }

  return String(left || "").localeCompare(String(right || ""), undefined, {
    numeric: true,
    sensitivity: "base",
  });
}

function MetricTile({ label, value, note, tone = "default" }) {
  const toneClass =
    tone === "accent"
      ? "tw-bg-[rgba(243,86,88,0.08)] tw-border-[rgba(243,86,88,0.12)]"
      : tone === "warm"
      ? "tw-bg-[rgba(250,216,69,0.16)] tw-border-[rgba(250,216,69,0.2)]"
      : "tw-bg-[rgba(44,50,85,0.04)] tw-border-[rgba(31,41,64,0.08)]";

  return (
    <div className={`tw-rounded-[20px] tw-border tw-p-4 ${toneClass}`}>
      <p className="admin-v2-eyebrow">{label}</p>
      <p className="tw-mt-2 tw-text-[15px] tw-font-medium tw-leading-6 tw-text-[var(--admin-v2-ink)]">
        {value}
      </p>
      {note ? <p className="tw-mt-1 tw-text-sm admin-v2-text-muted">{note}</p> : null}
    </div>
  );
}

function SectionToggle({ title, icon, open, onToggle, children, right }) {
  return (
    <section className="tw-rounded-[24px] tw-border tw-border-[rgba(31,41,64,0.08)] tw-bg-white tw-p-4 sm:tw-p-5">
      <button
        type="button"
        onClick={onToggle}
        className="tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-3 tw-text-left"
      >
        <div className="tw-flex tw-items-center tw-gap-2">
          {icon}
          <p className="tw-text-sm tw-font-semibold tw-text-[var(--admin-v2-ink)]">{title}</p>
        </div>
        <div className="tw-flex tw-items-center tw-gap-3">
          {right}
          <span
            className={`tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-full tw-bg-[rgba(44,50,85,0.06)] tw-text-[var(--admin-v2-ink)] tw-transition ${
              open ? "tw-rotate-180" : ""
            }`}
          >
            <ChevronDown size={16} />
          </span>
        </div>
      </button>
      {open ? <div className="tw-mt-4">{children}</div> : null}
    </section>
  );
}

function WorkspaceEmailActions({
  userId,
  userName,
  onCreatePassword,
  onWalletEmail,
  onCopyCreatePassword,
  onCopyWallet,
  createLoading = false,
  walletLoading = false,
  copyPasswordLoading = false,
  copyWalletLoading = false,
}) {
  const buttonClass =
    "tw-inline-flex tw-items-center tw-justify-center tw-rounded-2xl tw-border tw-px-3.5 tw-py-2.5 tw-text-xs tw-font-medium tw-transition disabled:tw-cursor-not-allowed disabled:tw-opacity-50";

  return (
    <div className="tw-mt-4 tw-rounded-[24px] tw-border tw-border-[rgba(31,41,64,0.08)] tw-bg-[linear-gradient(180deg,rgba(248,250,255,0.96),rgba(255,255,255,0.98))] tw-p-4">
      <div className="tw-grid tw-gap-4 lg:tw-grid-cols-2">
        <div className="tw-space-y-3">
          <div>
            <p className="admin-v2-eyebrow">Email actions</p>
            <p className="tw-mt-1 tw-text-sm admin-v2-text-muted">
              Send Cardly emails directly to {userName || "this user"}.
            </p>
          </div>
          <div className="tw-grid tw-gap-2">
            <button
              type="button"
              disabled={!userId || createLoading || walletLoading || copyPasswordLoading || copyWalletLoading}
              onClick={() => onCreatePassword(userId)}
              className={`${buttonClass} tw-border-[rgba(243,86,88,0.18)] tw-bg-[rgba(243,86,88,0.08)] tw-text-[#c44749]`}
            >
              {createLoading ? "Sending..." : "Send create password email"}
            </button>
            <button
              type="button"
              disabled={!userId || createLoading || walletLoading || copyPasswordLoading || copyWalletLoading}
              onClick={() => onWalletEmail(userId)}
              className={`${buttonClass} tw-border-[rgba(31,41,64,0.12)] tw-bg-white tw-text-[var(--admin-v2-ink)]`}
            >
              {walletLoading ? "Sending..." : "Send add to wallet email"}
            </button>
          </div>
        </div>

        <div className="tw-space-y-3">
          <div>
            <p className="admin-v2-eyebrow">Direct links</p>
            <p className="tw-mt-1 tw-text-sm admin-v2-text-muted">
              Copy a ready link if the user cannot access email right now.
            </p>
          </div>
          <div className="tw-grid tw-gap-2">
            <button
              type="button"
              disabled={!userId || createLoading || walletLoading || copyPasswordLoading || copyWalletLoading}
              onClick={() => onCopyCreatePassword(userId, userName)}
              className={`${buttonClass} tw-border-[rgba(44,50,85,0.12)] tw-bg-[rgba(44,50,85,0.05)] tw-text-[var(--admin-v2-ink)]`}
            >
              {copyPasswordLoading ? "Copying..." : "Copy create password link"}
            </button>
            <button
              type="button"
              disabled={!userId || createLoading || walletLoading || copyPasswordLoading || copyWalletLoading}
              onClick={() => onCopyWallet(userId, userName)}
              className={`${buttonClass} tw-border-[rgba(44,50,85,0.12)] tw-bg-[rgba(44,50,85,0.05)] tw-text-[var(--admin-v2-ink)]`}
            >
              {copyWalletLoading ? "Copying..." : "Copy add to wallet link"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CustomerProfileModal({
  user,
  onClose,
  onCreatePassword,
  onWalletEmail,
  onCopyCreatePassword,
  onCopyWallet,
  isMailing,
}) {
  if (!user) return null;

  return (
    <div className="tw-fixed tw-inset-0 tw-z-[60] tw-flex tw-items-center tw-justify-center tw-p-4">
      <button
        type="button"
        aria-label="Close user profile"
        className="tw-absolute tw-inset-0 tw-bg-[rgba(20,26,44,0.4)]"
        onClick={onClose}
      />
      <div className="tw-relative tw-z-[1] tw-w-full tw-max-w-[980px] tw-rounded-[32px] tw-border tw-border-[rgba(31,41,64,0.08)] tw-bg-[linear-gradient(180deg,#ffffff_0%,#f8faff_100%)] tw-p-5 tw-shadow-[0_28px_70px_rgba(20,26,44,0.18)] sm:tw-p-6">
        <div className="tw-flex tw-items-start tw-justify-between tw-gap-4">
          <div>
            <p className="admin-v2-eyebrow">User profile</p>
            <h3 className="tw-mt-2 tw-text-2xl tw-font-semibold tw-text-[var(--admin-v2-ink)]">{user.name}</h3>
            <p className="tw-mt-1 tw-text-sm admin-v2-text-muted">{user.jobTitle || user.email || "Workspace member"}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="tw-inline-flex tw-h-11 tw-w-11 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-[rgba(31,41,64,0.08)] tw-bg-white tw-text-[var(--admin-v2-ink)]"
          >
            <X size={16} />
          </button>
        </div>

        <div className="tw-mt-3 tw-flex tw-flex-wrap tw-gap-2">
          <StatusBadge status={formatRoleLabel(user.role)} />
          <StatusBadge status={user.disabled ? "Disabled" : "Active"} />
          <StatusBadge status={user.cardExist ? "Card active" : "No card"} />
        </div>

        <WorkspaceEmailActions
          userId={user.id}
          userName={user.name}
          onCreatePassword={onCreatePassword}
          onWalletEmail={onWalletEmail}
          onCopyCreatePassword={onCopyCreatePassword}
          onCopyWallet={onCopyWallet}
          createLoading={isMailing("password", user.id)}
          walletLoading={isMailing("wallet", user.id)}
          copyPasswordLoading={isMailing("copy-password", user.id)}
          copyWalletLoading={isMailing("copy-wallet", user.id)}
        />

        <div className="tw-mt-6 tw-grid tw-gap-6 lg:tw-grid-cols-[minmax(0,1.55fr)_360px]">
          <div className="tw-space-y-4">
            <div className="tw-rounded-[26px] tw-border tw-border-[rgba(31,41,64,0.08)] tw-bg-[rgba(248,250,255,0.9)] tw-p-4 sm:tw-p-5">
              <div className="tw-grid tw-gap-3 sm:tw-grid-cols-2">
                <MetricTile label="Primary email" value={user.email || "-"} />
                <MetricTile label="Phone" value={user.phone || "-"} />
                <MetricTile label="Created" value={formatDate(user.createdAt)} />
                <MetricTile label="Addresses" value={Array.isArray(user.addresses) ? user.addresses.length : 0} />
                <MetricTile label="Email slots" value={user.emailsCount || 0} />
                <MetricTile label="Phone slots" value={user.phonesCount || 0} />
              </div>
            </div>
          </div>
          <div className="tw-flex tw-items-start tw-justify-center lg:tw-justify-end">
            <AdminPassPreview preview={user.preview} />
          </div>
        </div>
      </div>
    </div>
  );
}

function WorkspaceAdminDetail({
  detail,
  selectedWorkspaceUserId,
  onSelectWorkspaceUser,
  onCreatePassword,
  onWalletEmail,
  onCopyCreatePassword,
  onCopyWallet,
  isMailing,
}) {
  const [userQuery, setUserQuery] = useState("");
  const [showSignals, setShowSignals] = useState(false);
  const [showReports, setShowReports] = useState(false);
  const [showWallets, setShowWallets] = useState(false);
  const [showUsers, setShowUsers] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [openGroups, setOpenGroups] = useState({});

  const selectedUser =
    detail?.tree?.flatMap((group) => group.users || []).find((user) => user.id === selectedWorkspaceUserId) ||
    detail?.tree?.[0]?.users?.[0] ||
    null;

  const filteredTree = useMemo(() => {
    const normalizedQuery = userQuery.trim().toLowerCase();
    if (!normalizedQuery) {
      return detail?.tree || [];
    }

    return (detail?.tree || [])
      .map((group) => ({
        ...group,
        users: (group.users || []).filter((user) =>
          [user.name, user.email, user.role]
            .filter(Boolean)
            .some((value) => String(value).toLowerCase().includes(normalizedQuery))
        ),
      }))
      .filter((group) => group.users.length);
  }, [detail?.tree, userQuery]);

  useEffect(() => {
    const nextGroups = {};
    (detail?.tree || []).forEach((group) => {
      nextGroups[group.id] = false;
    });
    setOpenGroups(nextGroups);
    setUserQuery("");
    setShowSignals(false);
    setShowReports(false);
    setShowWallets(false);
    setShowUsers(false);
    setShowProfile(false);
  }, [detail]);

  if (!detail) return null;

  return (
    <>
      <div className="tw-space-y-5">
        <div className="tw-flex tw-items-start tw-justify-between tw-gap-3">
          <div>
            <p className="admin-v2-eyebrow">Workspace overview</p>
            <h3 className="tw-mt-2 tw-text-2xl tw-font-semibold tw-tracking-tight tw-text-[var(--admin-v2-ink)]">
              {detail.overview.organizationName}
            </h3>
            <p className="tw-mt-1 tw-text-sm admin-v2-text-muted">
              {detail.accountOverview?.ownerName || detail.overview.name} | {detail.overview.packageName}
            </p>
          </div>
          <StatusBadge status={detail.overview.health} />
        </div>

        <div className="tw-grid tw-gap-3 sm:tw-grid-cols-2">
          <MetricTile label="Renewal" value={formatDays(detail.overview.daysLeft)} note={formatDate(detail.overview.nextPaymentDate)} />
          <MetricTile
            label="Workspace users"
            value={detail.accountOverview?.usersCount || 0}
            note={`${detail.accountOverview?.activeUsers || 0} active users`}
            tone="warm"
          />
        </div>

        <div className="tw-grid tw-gap-3 sm:tw-grid-cols-4">
          <MetricTile label="Active cards" value={detail.accountOverview?.activeCardholders || 0} />
          <MetricTile label="Reports" value={detail.accountOverview?.reportsCount || 0} />
          <MetricTile label="Designs" value={detail.overview.designsCount || 0} />
          <MetricTile label="Disabled users" value={detail.accountOverview?.disabledUsers || 0} />
        </div>

        <SectionToggle
          title="Workspace signals"
          icon={<Globe size={16} className="tw-text-[var(--admin-v2-accent)]" />}
          open={showSignals}
          onToggle={() => setShowSignals((value) => !value)}
        >
          <div className="tw-grid tw-gap-3 sm:tw-grid-cols-2">
            <MetricTile label="Owner" value={detail.accountOverview?.ownerName || "-"} note={detail.accountOverview?.ownerEmail || "-"} />
            <MetricTile label="Segment" value={detail.overview.segment} />
            <MetricTile label="Company URL" value={formatUrlLabel(detail.overview.companyURL)} />
            <MetricTile label="LinkedIn" value={formatUrlLabel(detail.overview.linkedInURL)} />
          </div>
        </SectionToggle>

        <SectionToggle
          title="Reports and roles"
          icon={<PieChart size={16} className="tw-text-[var(--admin-v2-accent)]" />}
          open={showReports}
          onToggle={() => setShowReports((value) => !value)}
        >
          <div className="tw-grid tw-gap-3 sm:tw-grid-cols-2">
            <MetricTile label="Reports total" value={detail.accountOverview?.reportsCount || 0} />
            <MetricTile label="Reports (30d)" value={detail.accountOverview?.reportsLast30Days || 0} />
            <MetricTile label="Latest report" value={formatDate(detail.accountOverview?.latestReportAt)} />
            <MetricTile label="First report" value={formatDate(detail.accountOverview?.firstReportAt)} />
          </div>
          <div className="tw-mt-4 tw-space-y-2">
            {(detail.analytics?.roles || []).map((item) => (
              <div
                key={item.role}
                className="tw-flex tw-items-center tw-justify-between tw-rounded-2xl tw-bg-[rgba(44,50,85,0.04)] tw-px-3 tw-py-2.5"
              >
                <span className="tw-text-sm tw-font-medium tw-text-[var(--admin-v2-ink)]">{formatRoleLabel(item.label)}</span>
                <span className="tw-text-sm tw-font-semibold tw-text-[var(--admin-v2-ink)]">{item.count}</span>
              </div>
            ))}
          </div>
        </SectionToggle>

        <SectionToggle
          title="Wallets and locations"
          icon={<WalletCards size={16} className="tw-text-[var(--admin-v2-accent)]" />}
          open={showWallets}
          onToggle={() => setShowWallets((value) => !value)}
        >
          <div className="tw-grid tw-gap-3 sm:tw-grid-cols-2">
            <MetricTile label="Apple devices" value={detail.analytics?.wallets?.appleDevices || 0} />
            <MetricTile label="Google devices" value={detail.analytics?.wallets?.googleDevices || 0} />
          </div>
          <div className="tw-mt-4 tw-space-y-2">
            {(detail.analytics?.locations || []).length ? (
              detail.analytics.locations.map((item) => (
                <div
                  key={item.label}
                  className="tw-flex tw-items-center tw-justify-between tw-rounded-2xl tw-bg-[rgba(44,50,85,0.04)] tw-px-3 tw-py-2.5"
                >
                  <span className="tw-truncate tw-pr-3 tw-text-sm tw-font-medium tw-text-[var(--admin-v2-ink)]">{item.label}</span>
                  <span className="tw-text-sm tw-font-semibold tw-text-[var(--admin-v2-ink)]">{item.count}</span>
                </div>
              ))
            ) : (
              <div className="tw-rounded-2xl tw-bg-[rgba(44,50,85,0.04)] tw-px-3 tw-py-3 tw-text-sm admin-v2-text-muted">
                No location data available.
              </div>
            )}
          </div>
        </SectionToggle>

        <SectionToggle
          title="User tree"
          icon={<FolderTree size={16} className="tw-text-[var(--admin-v2-accent)]" />}
          open={showUsers}
          onToggle={() => setShowUsers((value) => !value)}
          right={
            <span className="tw-text-xs tw-font-medium tw-text-[var(--admin-v2-muted)]">
              {detail.accountOverview?.usersCount || 0} total
            </span>
          }
        >
          <div className="tw-space-y-4">
            <input
              value={userQuery}
              onChange={(event) => setUserQuery(event.target.value)}
              placeholder="Search users by name, email, or role..."
              className="tw-w-full tw-rounded-2xl tw-border tw-border-[rgba(31,41,64,0.12)] tw-bg-white tw-px-4 tw-py-3 tw-text-sm tw-text-[var(--admin-v2-ink)]"
            />
            <div className="tw-space-y-3">
              {filteredTree.map((group) => {
                const isOpen = Boolean(openGroups[group.id]);
                return (
                  <div key={group.id} className="tw-rounded-[20px] tw-bg-[rgba(44,50,85,0.04)] tw-p-3">
                    <button
                      type="button"
                      onClick={() =>
                        setOpenGroups((current) => ({
                          ...current,
                          [group.id]: !current[group.id],
                        }))
                      }
                      className="tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-3"
                    >
                      <div>
                        <p className="tw-text-left tw-text-sm tw-font-semibold tw-uppercase tw-tracking-[0.12em] tw-text-[var(--admin-v2-muted)]">
                          {formatRoleLabel(group.label)}
                        </p>
                        <p className="tw-mt-1 tw-text-left tw-text-xs admin-v2-text-muted">{group.count} users</p>
                      </div>
                      <span className={`tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-full tw-bg-white tw-transition ${isOpen ? "tw-rotate-90" : ""}`}>
                        <ChevronRight size={16} />
                      </span>
                    </button>
                    {isOpen ? (
                      <div className="tw-mt-3 tw-space-y-2">
                        {group.users.map((user) => (
                          <button
                            key={user.id}
                            type="button"
                            onClick={() => {
                              onSelectWorkspaceUser(user.id);
                              setShowProfile(true);
                            }}
                            className={`tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-3 tw-rounded-2xl tw-px-3 tw-py-3 tw-text-left tw-transition ${
                              selectedUser?.id === user.id ? "tw-bg-white tw-shadow-[0_10px_20px_rgba(20,26,44,0.08)]" : "hover:tw-bg-white/70"
                            }`}
                          >
                            <div className="tw-min-w-0">
                              <p className="tw-truncate tw-text-sm tw-font-medium tw-text-[var(--admin-v2-ink)]">{user.name}</p>
                              <p className="tw-truncate tw-text-xs admin-v2-text-muted">{user.email}</p>
                            </div>
                            <ChevronRight size={16} className="tw-text-[var(--admin-v2-muted)]" />
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        </SectionToggle>
      </div>

      {showProfile ? (
        <CustomerProfileModal
          user={selectedUser}
          onClose={() => setShowProfile(false)}
          onCreatePassword={onCreatePassword}
          onWalletEmail={onWalletEmail}
          onCopyCreatePassword={onCopyCreatePassword}
          onCopyWallet={onCopyWallet}
          isMailing={isMailing}
        />
      ) : null}
    </>
  );
}

function IndividualCustomerDetail({
  detail,
  onCreatePassword,
  onWalletEmail,
  onCopyCreatePassword,
  onCopyWallet,
  isMailing,
}) {
  const overview = detail?.overview;


console.log("FULL DETAIL:", detail);
console.log("OVERVIEW:", overview);
console.log("PREVIEW:", overview?.preview);




  if (!overview) return null;

  return (
    <div className="tw-space-y-5">
      <div className="tw-flex tw-items-start tw-justify-between tw-gap-3">
        <div>
          <p className="admin-v2-eyebrow">Customer profile</p>
          <h3 className="tw-mt-2 tw-text-2xl tw-font-semibold tw-tracking-tight tw-text-[var(--admin-v2-ink)]">
            {overview.name}
          </h3>
          <p className="tw-mt-1 tw-text-sm admin-v2-text-muted">
            {overview.organizationName} | {overview.segment}
          </p>
        </div>
        <StatusBadge status={overview.health} />
      </div>

      <WorkspaceEmailActions
        userId={overview.id}
        userName={overview.name}
        onCreatePassword={onCreatePassword}
        onWalletEmail={onWalletEmail}
        onCopyCreatePassword={onCopyCreatePassword}
        onCopyWallet={onCopyWallet}
        createLoading={isMailing("password", overview.id)}
        walletLoading={isMailing("wallet", overview.id)}
        copyPasswordLoading={isMailing("copy-password", overview.id)}
        copyWalletLoading={isMailing("copy-wallet", overview.id)}
      />

      <div className="tw-grid tw-gap-6 lg:tw-grid-cols-[minmax(0,1.55fr)_360px]">
        <div className="tw-space-y-4">
          <div className="tw-grid tw-gap-3 sm:tw-grid-cols-2">
            <MetricTile label="Primary email" value={overview.email || "-"} />
            <MetricTile label="Phone" value={overview.phone || "-"} />
            <MetricTile label="Role" value={formatRoleLabel(overview.role)} />
            <MetricTile label="Job title" value={overview.jobTitle || "-"} />
            <MetricTile label="Renewal" value={formatDays(overview.daysLeft)} note={formatDate(overview.nextPaymentDate)} />
            <MetricTile label="Workspace owner" value={detail?.accountOverview?.ownerName || "-"} note={detail?.accountOverview?.ownerEmail || "-"} />
          </div>

          <SectionToggle
            title="User signals"
            icon={<Users size={16} className="tw-text-[var(--admin-v2-accent)]" />}
            open
            onToggle={() => {}}
          >
            <div className="tw-grid tw-gap-3 sm:tw-grid-cols-3">
              <MetricTile label="Reports" value={overview.reportsCount || 0} />
              <MetricTile label="Designs" value={overview.designsCount || 0} />
              <MetricTile label="Addresses" value={Array.isArray(overview.addresses) ? overview.addresses.length : 0} />
              <MetricTile label="Emails" value={overview.emailsCount || 0} />
              <MetricTile label="Phones" value={overview.phonesCount || 0} />
              <MetricTile label="Created" value={formatDate(overview.createdAt)} />
            </div>
          </SectionToggle>

          <div className="tw-grid tw-gap-3 sm:tw-grid-cols-2">
            <MetricTile label="Email auth" value={overview.emailAuth ? "Enabled" : "Disabled"} />
            <MetricTile label="Third party auth" value={overview.thirdPartyAuth ? "Enabled" : "Disabled"} />
            <MetricTile label="Company URL" value={formatUrlLabel(overview.companyURL)} />
            <MetricTile label="LinkedIn" value={formatUrlLabel(overview.linkedInURL)} />
          </div>
        </div>

        <div className="tw-flex tw-items-start tw-justify-center lg:tw-justify-end">
          <AdminPassPreview preview={overview.preview} />
        </div>
      </div>
    </div>
  );
}

function CustomerDetailDrawer({
  open,
  detail,
  loading,
  onClose,
  selectedWorkspaceUserId,
  onSelectWorkspaceUser,
  onCreatePassword,
  onWalletEmail,
  onCopyCreatePassword,
  onCopyWallet,
  isMailing,
}) {
  const isWorkspaceAdmin = isWorkspaceAdminRole(detail?.overview?.role);

  if (!open) return null;

  return createPortal(
    <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-justify-end">
      <button
        type="button"
        aria-label="Close customer detail"
        className="tw-absolute tw-inset-0 tw-bg-[rgba(20,26,44,0.72)]"
        onClick={onClose}
      />
      <aside
        className="tw-relative tw-z-[1] tw-h-[calc(100vh+20px)] tw-w-full tw-max-w-[920px] tw-overflow-y-auto tw-border-l tw-border-[rgba(31,41,64,0.08)] tw-bg-[linear-gradient(180deg,#ffffff_0%,#f8faff_100%)] tw-p-0"
        style={{
          borderTopLeftRadius: 28,
          borderBottomLeftRadius: 28,
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0,
        }}
      >
        <div className="tw-sticky tw-top-0 tw-z-[2] tw-mb-5 tw-flex tw-items-start tw-justify-between tw-gap-4 tw-border-b tw-border-[rgba(31,41,64,0.08)] tw-bg-white tw-px-4 tw-py-5 sm:tw-px-5 lg:tw-px-6">
          <div>
            <p className="admin-v2-eyebrow">Customer detail</p>
            <h2 className="tw-mt-2 tw-text-xl tw-font-semibold tw-text-[var(--admin-v2-ink)] sm:tw-text-3xl">
              {detail?.overview?.name || "Loading customer"}
            </h2>
            <div className="tw-mt-3 tw-inline-flex tw-items-center tw-rounded-2xl tw-border tw-border-[rgba(31,41,64,0.08)] tw-bg-[rgba(248,250,255,0.96)] tw-px-4 tw-py-2.5">
              <p className="tw-text-sm tw-font-medium tw-text-[var(--admin-v2-muted)]">
                {isWorkspaceAdmin
                  ? "Workspace admin detail with team, role tree, and customer signals."
                  : "Personal or free user profile, access state, and saved pass preview."}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="tw-inline-flex tw-h-11 tw-w-11 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-[rgba(31,41,64,0.08)] tw-bg-white tw-text-[var(--admin-v2-ink)]"
          >
            <X size={18} />
          </button>
        </div>

        <div className="tw-space-y-5 tw-px-4 tw-pb-4 sm:tw-px-5 lg:tw-px-6 lg:tw-pb-6">
          {loading ? (
            <div className="admin-v2-text-muted">Loading customer detail...</div>
          ) : !detail ? (
            <div className="admin-v2-text-muted">Select a customer to inspect details.</div>
          ) : isWorkspaceAdmin ? (
            <WorkspaceAdminDetail
              detail={detail}
              selectedWorkspaceUserId={selectedWorkspaceUserId}
              onSelectWorkspaceUser={onSelectWorkspaceUser}
              onCreatePassword={onCreatePassword}
              onWalletEmail={onWalletEmail}
              onCopyCreatePassword={onCopyCreatePassword}
              onCopyWallet={onCopyWallet}
              isMailing={isMailing}
            />
          ) : (
            <IndividualCustomerDetail
              detail={detail}
              onCreatePassword={onCreatePassword}
              onWalletEmail={onWalletEmail}
              onCopyCreatePassword={onCopyCreatePassword}
              onCopyWallet={onCopyWallet}
              isMailing={isMailing}
            />
          )}
        </div>
      </aside>
    </div>,
    document.body
  );
}

export default function CustomersPage() {
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState("");
  const [accountFilter, setAccountFilter] = useState("all");
  const [structureFilter, setStructureFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedWorkspaceUserId, setSelectedWorkspaceUserId] = useState("");
  const [sortKey, setSortKey] = useState("person");
  const [sortDirection, setSortDirection] = useState("asc");
  const [mailingState, setMailingState] = useState({});

  const loadRows = async (mode = "initial") => {
    try {
      mode === "initial" ? setLoading(true) : setRefreshing(true);
      const data = await getCustomersV2();
      setRows(Array.isArray(data) ? data : []);
    } catch (error) {
      showToastMessage(error?.message || "Failed to load customers");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadRows();
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    let cancelled = false;

    const loadDetail = async () => {
      try {
        setDetailLoading(true);
        const data = await getCustomerDetailV2(selectedId);
        if (!cancelled) {
          setDetail(data);
          setSelectedWorkspaceUserId(
            data?.tree?.flatMap((group) => group.users || [])?.[0]?.id || ""
          );
        }
      } catch (error) {
        if (!cancelled) {
          showToastMessage(error?.message || "Failed to load customer detail");
        }
      } finally {
        if (!cancelled) {
          setDetailLoading(false);
        }
      }
    };

    loadDetail();

    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  const filteredRows = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    return rows.filter((row) => {
      const bucket = getCustomerBucket(row);
      if (bucket === "employee" || bucket === "managed_user") return false;

      const matchesQuery =
        !normalizedQuery ||
        [row.name, row.email, row.organizationName, row.packageName, row.role, row.segment]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(normalizedQuery));

      const matchesAccount =
        accountFilter === "all" || String(row.accountType || "").toLowerCase() === accountFilter;

      const normalizedBucket = bucket === "free" ? "personal" : bucket;
      const matchesStructure = structureFilter === "all" || normalizedBucket === structureFilter;

      return matchesQuery && matchesAccount && matchesStructure;
    });
  }, [accountFilter, query, rows, structureFilter]);

  const sortedRows = useMemo(() => {
    const getSortValue = (row) => {
      switch (sortKey) {
        case "workspace":
          return row.organizationName;
        case "structure":
          return row.segment;
        case "role":
          return formatRoleLabel(row.role);
        case "renewal":
          return Number(row.daysLeft ?? 0);
        case "health":
          return row.health;
        case "person":
        default:
          return row.name || row.email;
      }
    };

    const nextRows = [...filteredRows].sort((left, right) => {
      const comparison = compareValues(getSortValue(left), getSortValue(right));
      return sortDirection === "asc" ? comparison : -comparison;
    });

    return nextRows;
  }, [filteredRows, sortDirection, sortKey]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDirection((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }

    setSortKey(key);
    setSortDirection("asc");
  };

  const openCustomerDetail = (row) => {
    setSelectedId(row.id);
    setDetailOpen(true);
  };

  const setMailing = useCallback((type, userId, value) => {
    const key = `${type}:${userId}`;
    setMailingState((current) => ({
      ...current,
      [key]: value,
    }));
  }, []);

  const isMailing = useCallback(
    (type, userId) => Boolean(mailingState[`${type}:${userId}`]),
    [mailingState]
  );

  const handleWorkspaceEmail = useCallback(
    async (userId, type) => {
      if (!userId) return;

      try {
        setMailing(type, userId, true);
        if (type === "password") {
          const result = await resendWorkspaceCreatePasswordV2(userId);
          showToastMessage(result?.message || "Create password email sent successfully.");
          return;
        }

        const result = await resendWorkspaceWalletEmailV2(userId);
        showToastMessage(result?.message || "Add to wallet email sent successfully.");
      } catch (error) {
        showToastMessage(error?.message || "Unable to send workspace email.");
      } finally {
        setMailing(type, userId, false);
      }
    },
    [setMailing]
  );

  const handleCopyWorkspaceLink = useCallback(
    async (userId, type, userName) => {
      if (!userId) return;

      try {
        setMailing(type, userId, true);

        const payload =
          type === "copy-password"
            ? await generateWorkspaceCreatePasswordLinkV2(userId)
            : await generateWorkspaceWalletLinkV2(userId);

        const label =
          type === "copy-password"
            ? `${userName || payload?.name || "User"} - Create Password`
            : `${userName || payload?.name || "User"} - Add to Wallet`;

        await navigator.clipboard.writeText(payload?.link || "");
        showToastMessage(`${label} link copied successfully.`);
      } catch (error) {
        showToastMessage(error?.message || "Unable to copy workspace link.");
      } finally {
        setMailing(type, userId, false);
      }
    },
    [setMailing]
  );

  const summary = useMemo(
    () => [
      {
        label: "Customer records",
        value: filteredRows.length,
        note: "Only workspace admins and personal/free users",
        icon: <Users size={18} />,
      },
      {
        label: "Workspace admins",
        value: filteredRows.filter((row) => isWorkspaceAdminRole(row.role)).length,
        note: "Enterprise and organization owners",
        icon: <ShieldCheck size={18} />,
      },
      {
        label: "Personal / free",
        value: filteredRows.filter((row) => ["personal", "free"].includes(getCustomerBucket(row))).length,
        note: "Standalone customer directory",
        icon: <Globe size={18} />,
      },
      {
        label: "Expiring soon",
        value: filteredRows.filter((row) => typeof row.daysLeft === "number" && row.daysLeft >= 0 && row.daysLeft <= 14).length,
        note: "Renewal due in the next 14 days",
        icon: <Sparkles size={18} />,
      },
    ],
    [filteredRows]
  );

  return (
    <div className="tw-space-y-6">
      <section className="admin-v2-panel tw-p-5 sm:tw-p-6 lg:tw-p-8">
        <div className="tw-grid tw-gap-6 xl:tw-grid-cols-[minmax(0,1fr)_auto] xl:tw-items-end">
          <div className="tw-space-y-3">
            <p className="admin-v2-eyebrow">Customers Accounts</p>
            <h1 className="admin-v2-title">Manage Customers</h1>
            <p className="admin-v2-subtitle">
              Click any customer row to inspect profile, renewal, pass preview, or full workspace detail if the user is an admin.
            </p>
          </div>
          <div className="tw-flex tw-flex-wrap tw-gap-3">
            <button
              type="button"
              className="admin-v2-action admin-v2-action-secondary"
              onClick={() => loadRows("refresh")}
            >
              <RefreshCw size={16} className={refreshing ? "tw-animate-spin" : ""} />
              Refresh
            </button>
          </div>
        </div>
      </section>

      <section className="admin-v2-soft-panel tw-p-4 lg:tw-p-6">
        <div className="tw-grid tw-gap-4 md:tw-grid-cols-2 xl:tw-grid-cols-4">
          {summary.map((item) => (
            <div key={item.label} className="admin-v2-stat-panel">
              <div className="tw-flex tw-items-center tw-justify-between">
                <p className="admin-v2-eyebrow">{item.label}</p>
                <span className="tw-text-[var(--admin-v2-accent)]">{item.icon}</span>
              </div>
              <p className="tw-mt-5 tw-text-4xl tw-font-semibold tw-text-[var(--admin-v2-ink)]">{item.value}</p>
              <p className="tw-mt-2 tw-text-sm admin-v2-text-muted">{item.note}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="admin-v2-soft-panel tw-p-4 lg:tw-p-6">
        <div className="tw-flex tw-flex-col tw-gap-3 lg:tw-flex-row lg:tw-items-center lg:tw-justify-between">
          <div className="tw-flex tw-flex-1 tw-flex-col tw-gap-3 sm:tw-flex-row">
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search person, workspace, email, or role..."
              className="tw-w-full tw-rounded-2xl tw-border tw-border-[rgba(31,41,64,0.12)] tw-bg-white tw-px-4 tw-py-3 tw-text-sm tw-text-[var(--admin-v2-ink)] focus:tw-border-[rgba(243,86,88,0.5)] focus:tw-outline-none focus:tw-ring-4 focus:tw-ring-[rgba(243,86,88,0.08)] sm:tw-max-w-[380px]"
            />
            <select
              value={accountFilter}
              onChange={(event) => setAccountFilter(event.target.value)}
              className="tw-rounded-2xl tw-border tw-border-[rgba(31,41,64,0.12)] tw-bg-white tw-px-4 tw-py-3 tw-text-sm tw-text-[var(--admin-v2-ink)]"
            >
              <option value="all">All account types</option>
              <option value="personal">Personal</option>
              <option value="organization">Organization</option>
              <option value="enterprise">Enterprise</option>
            </select>
            <select
              value={structureFilter}
              onChange={(event) => setStructureFilter(event.target.value)}
              className="tw-rounded-2xl tw-border tw-border-[rgba(31,41,64,0.12)] tw-bg-white tw-px-4 tw-py-3 tw-text-sm tw-text-[var(--admin-v2-ink)]"
            >
              <option value="all">All structures</option>
              <option value="workspace_admin">Workspace admins</option>
              <option value="personal">Personal / free users</option>
            </select>
          </div>

          <div className="admin-v2-brand-badge">
            <span className="admin-v2-brand-dot" />
            {sortedRows.length} customer records
          </div>
        </div>

        <div className="tw-mt-5">
          {loading ? (
            <div className="tw-p-8 admin-v2-text-muted">Loading customers...</div>
          ) : !sortedRows.length ? (
            <div className="tw-p-8 admin-v2-text-muted">No customers match the current filters.</div>
          ) : (
            <>
              <div className="admin-v2-table-desktop tw-overflow-x-auto">
                <table className="admin-v2-data-table">
                  <thead>
                    <tr>
                      {[
                        ["person", "Person"],
                        ["workspace", "Workspace"],
                        ["structure", "Structure"],
                        ["role", "Role"],
                        ["renewal", "Renewal"],
                        ["health", "Health"],
                      ].map(([key, label]) => (
                        <th key={key}>
                          <button
                            type="button"
                            onClick={() => handleSort(key)}
                            className="tw-inline-flex tw-items-center tw-gap-2 tw-text-left tw-text-inherit"
                          >
                            <span>{label}</span>
                            <ArrowUpDown
                              size={13}
                              className={
                                sortKey === key
                                  ? "tw-text-[var(--admin-v2-accent)]"
                                  : "tw-text-[var(--admin-v2-muted)]"
                              }
                            />
                          </button>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedRows.map((row) => (
                      <tr
                        key={row.id}
                        className={`tw-cursor-pointer ${selectedId === row.id && detailOpen ? "tw-bg-[rgba(243,86,88,0.04)]" : ""}`}
                        onClick={() => openCustomerDetail(row)}
                      >
                        <td>
                          <div className="tw-space-y-1.5">
                            <p className="tw-font-semibold tw-text-[var(--admin-v2-ink)]">{row.name}</p>
                            <p className="tw-text-sm admin-v2-text-muted">{row.email}</p>
                          </div>
                        </td>
                        <td>
                          <div className="tw-space-y-1.5">
                            <p className="tw-font-medium tw-text-[var(--admin-v2-ink)]">{row.organizationName}</p>
                            <p className="tw-text-sm admin-v2-text-muted">{formatAccountTypeLabel(row.accountType)}</p>
                          </div>
                        </td>
                        <td>
                          <StatusBadge status={row.segment} />
                        </td>
                        <td>
                          <StatusBadge status={formatRoleLabel(row.role)} />
                        </td>
                        <td>
                          <div className="tw-space-y-1">
                            <p className="tw-font-medium tw-text-[var(--admin-v2-ink)]">{formatDays(row.daysLeft)}</p>
                            <p className="tw-text-sm admin-v2-text-muted">{formatDate(row.nextPaymentDate)}</p>
                          </div>
                        </td>
                        <td>
                          <StatusBadge status={row.health} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="admin-v2-data-grid md:tw-hidden">
                {sortedRows.map((row) => (
                  <article
                    key={row.id}
                    onClick={() => openCustomerDetail(row)}
                    className={`admin-v2-mobile-card tw-space-y-4 tw-cursor-pointer ${
                      selectedId === row.id && detailOpen ? "tw-ring-2 tw-ring-[rgba(243,86,88,0.16)]" : ""
                    }`}
                  >
                    <div className="tw-flex tw-items-start tw-justify-between tw-gap-3">
                      <div>
                        <p className="tw-text-base tw-font-semibold tw-text-[var(--admin-v2-ink)]">{row.name}</p>
                        <p className="tw-text-sm admin-v2-text-muted">{row.email}</p>
                      </div>
                      <StatusBadge status={row.health} />
                    </div>

                    <div className="tw-flex tw-flex-wrap tw-gap-2">
                      <StatusBadge status={row.segment} />
                      <StatusBadge status={formatRoleLabel(row.role)} />
                    </div>

                    <div className="tw-flex tw-items-center tw-justify-between tw-text-sm">
                      <span className="tw-font-medium tw-text-[var(--admin-v2-ink)]">{row.organizationName}</span>
                      <span className="admin-v2-text-muted">{formatDays(row.daysLeft)}</span>
                    </div>
                  </article>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      <CustomerDetailDrawer
        open={detailOpen}
        detail={detail}
        loading={detailLoading}
        onClose={() => {
          setDetailOpen(false);
          setSelectedId("");
          setDetail(null);
          setSelectedWorkspaceUserId("");
        }}
        selectedWorkspaceUserId={selectedWorkspaceUserId}
        onSelectWorkspaceUser={setSelectedWorkspaceUserId}
        onCreatePassword={(userId) => handleWorkspaceEmail(userId, "password")}
        onWalletEmail={(userId) => handleWorkspaceEmail(userId, "wallet")}
        onCopyCreatePassword={(userId, userName) => handleCopyWorkspaceLink(userId, "copy-password", userName)}
        onCopyWallet={(userId, userName) => handleCopyWorkspaceLink(userId, "copy-wallet", userName)}
        isMailing={isMailing}
      />
    </div>
  );
}
